/**
 * useIdentifyUser Hook
 *
 * Handles user identification in PostHog when user logs in.
 * Associates analytics events with authenticated user profile.
 *
 * User Story: US2 - User Identification
 */

import { useCallback, useEffect } from 'react';

/**
 * User identification data
 */
export interface UserIdentification {
  userId: string;
  email: string;
  organizationId: string;
  displayName?: string;
  roles?: string[];
}

/**
 * useIdentifyUser hook
 *
 * Identifies the current user in PostHog and sends their profile data.
 * Called when user logs in or authentication state changes.
 *
 * @param user - User object with identification data, or null if not authenticated
 *
 * @example
 * const user = useAuthStore((state) => state.user);
 * useIdentifyUser(user);
 *
 * // In PostHog events, this user will be identified with:
 * // - User ID: user.userId
 * // - Properties: email, organization, displayName, roles
 */
export function useIdentifyUser(user: UserIdentification | null): void {
  const identifyUser = useCallback((): void => {
    if (!user) {
      return;
    }

    try {
      // Check if PostHog is available
      if (typeof window === 'undefined' || !window.posthog) {
        return;
      }

      const posthog = window.posthog;
      if (typeof posthog.identify !== 'function') {
        return;
      }

      // Identify user in PostHog
      posthog.identify(user.userId, {
        email: user.email,
        organizationId: user.organizationId,
        displayName: user.displayName ?? user.email.split('@')[0],
        roles: user.roles ?? [],
      });

      // Also set super properties (included in all events)
      if (posthog.setPersonProperties && typeof posthog.setPersonProperties === 'function') {
        posthog.setPersonProperties({
          organization: user.organizationId,
        });
      }
    } catch (error) {
      // Fail silently - analytics should not break functionality
      // eslint-disable-next-line no-console
      console.debug('Failed to identify user in analytics:', error);
    }
  }, [user]);

  // Identify user when user changes
  useEffect(() => {
    identifyUser();
  }, [identifyUser]);
}

/**
 * useResetAnalyticsUser hook
 *
 * Returns a function to reset user identification when user logs out.
 *
 * @example
 * const resetAnalytics = useResetAnalyticsUser();
 * const onLogout = () => {
 *   resetAnalytics();
 *   // ... other logout logic
 * }
 */
export function useResetAnalyticsUser(): () => void {
  const resetUser = useCallback((): void => {
    try {
      if (typeof window === 'undefined' || !window.posthog) {
        return;
      }

      const posthog = window.posthog;
      if (posthog.reset && typeof posthog.reset === 'function') {
        posthog.reset();
      }
    } catch (error) {
      // Fail silently
      // eslint-disable-next-line no-console
      console.debug('Failed to reset analytics user:', error);
    }
  }, []);

  return resetUser;
}
